import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearch, type SearchResult, type SearchResults } from '../../hooks/useSearch.js';
import { useRecentStore } from '../../stores/recent.js';
import { useFeatureFlag } from '../../lib/posthog.js';

// ── Result item ────────────────────────────────────────────────────────────────

function ResultItem({
  result,
  isSelected,
  onSelect,
}: {
  result: SearchResult;
  isSelected: boolean;
  onSelect: (r: SearchResult) => void;
}) {
  return (
    <button
      onMouseDown={(e) => { e.preventDefault(); onSelect(result); }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        width: '100%',
        padding: 'var(--space-2) var(--space-4)',
        background: isSelected ? 'var(--color-bg-subtle)' : 'none',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', fontWeight: 'var(--font-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {result.title}
        </p>
        {(result.subtitle || result.meta) && (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[result.subtitle, result.meta].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
    </button>
  );
}

// ── Section ────────────────────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ padding: 'var(--space-2) var(--space-4) var(--space-1)', borderTop: '1px solid var(--color-border)' }}>
        <p style={{ fontSize: '11px', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {label}
        </p>
      </div>
      {children}
    </div>
  );
}

// ── Flatten results for keyboard nav ──────────────────────────────────────────

function flattenResults(r: SearchResults): SearchResult[] {
  return [...r.companies, ...r.contacts, ...r.buckets, ...r.orders];
}

// ── SearchBar ─────────────────────────────────────────────────────────────────

export default function SearchBar({ inputRef: externalRef }: { inputRef?: React.RefObject<HTMLInputElement> }) {
  const { t } = useTranslation('search');
  const navigate = useNavigate();
  const addRecent = useRecentStore((s) => s.addItem);
  const searchEnabled = useFeatureFlag('search');

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = externalRef ?? internalRef;
  const containerRef = useRef<HTMLDivElement>(null);

  const { results, isLoading } = useSearch(searchEnabled ? query : '');
  const flat = flattenResults(results);
  const hasResults = flat.length > 0;

  const handleSelect = useCallback((result: SearchResult) => {
    addRecent({ id: result.id, type: result.type, title: result.title, url: result.url });
    navigate(result.url);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  }, [addRecent, navigate, inputRef]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = flat[selectedIdx];
      if (r) handleSelect(r);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Reset selection when results change
  useEffect(() => { setSelectedIdx(0); }, [results]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!searchEnabled) return null;

  const showDropdown = isOpen && query.trim().length > 0;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: 280 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-3)',
          background: 'var(--color-bg-subtle)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <Search size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('placeholder')}
          style={{
            flex: 1,
            border: 'none',
            background: 'none',
            outline: 'none',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text)',
          }}
        />
      </div>

      {showDropdown && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--space-1))',
            left: 0,
            right: 0,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 200,
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          {isLoading && (
            <p style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              …
            </p>
          )}

          {!isLoading && !hasResults && (
            <div style={{ padding: 'var(--space-4) var(--space-4)' }}>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
                {t('no_results.title', { query })}
              </p>
              <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 1.8 }}>
                <li>{t('no_results.tip_typos')}</li>
                <li>{t('no_results.tip_order')}</li>
                <li>{t('no_results.tip_email')}</li>
              </ul>
            </div>
          )}

          {!isLoading && hasResults && (
            <>
              {results.companies.length > 0 && (
                <Section label={t('sections.companies')}>
                  {results.companies.map((r) => (
                    <ResultItem
                      key={r.id}
                      result={r}
                      isSelected={flat.indexOf(r) === selectedIdx}
                      onSelect={handleSelect}
                    />
                  ))}
                </Section>
              )}
              {results.contacts.length > 0 && (
                <Section label={t('sections.contacts')}>
                  {results.contacts.map((r) => (
                    <ResultItem
                      key={r.id}
                      result={r}
                      isSelected={flat.indexOf(r) === selectedIdx}
                      onSelect={handleSelect}
                    />
                  ))}
                </Section>
              )}
              {results.buckets.length > 0 && (
                <Section label={t('sections.buckets')}>
                  {results.buckets.map((r) => (
                    <ResultItem
                      key={r.id}
                      result={r}
                      isSelected={flat.indexOf(r) === selectedIdx}
                      onSelect={handleSelect}
                    />
                  ))}
                </Section>
              )}
              {results.orders.length > 0 && (
                <Section label={t('sections.orders')}>
                  {results.orders.map((r) => (
                    <ResultItem
                      key={r.id}
                      result={r}
                      isSelected={flat.indexOf(r) === selectedIdx}
                      onSelect={handleSelect}
                    />
                  ))}
                </Section>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
